import { type ReactNode, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  duplicatePhonePolicyMessage,
  normalizeTtMobileNumber,
} from '@trinidad/shared/phone-normalization';
import { APPROVED_SOURCES, type ApprovedSource, TT_REGIONS } from '@trinidad/shared/sources';

import { buildApiUrl } from '@/lib/api-base';
import { collectBrowserFingerprint } from '@/lib/browser-fingerprint';
import { markGoogleAdsVerifiedSignupPending } from '@/lib/google-ads';

import {
  PRODUCTION_SMOKE_HCAPTCHA_SITE_KEY,
  getHcaptchaSiteKey,
  readProductionSignupSmokeConfig,
} from '../components/hcaptcha-config';
import { HcaptchaWidget } from '../components/hcaptcha-widget';
import { ResidencyRejectionScreen } from '../components/residency-rejection';
import { ProductStatsBand, ProductUiComposition } from './product-ui-imagery';
import {
  BullseyeDevice,
  BullseyeMarker,
  DesignFooter,
  DesignNav,
  RedPeriod,
  SectionHead,
  TopicIcon,
  useDocumentTitle,
} from './website-design';

export const PRODUCTION_TARGET_CONSENT = {
  body: 'Trini Rewards is operated as an independent T&T research panel. By joining, you consent to receiving study invitations by email when your profile matches a study, and to your responses being used in aggregate research reports. Your individual responses are never shared with brands. We hold your name, email, region, and nominated mobile number for the duration of your panel membership; you may delete your account at any time. Trini Points are credited to your wallet on study completion and are redeemable from 500 points as prepaid mobile airtime for your Trinidad and Tobago mobile number. Our airtime processor receives only the mobile number, amount, and transaction references needed to fulfil your chosen redemption.',
  version: 'v0.1-2026-05-03-trinirewards',
} as const;

type ActiveConsentResponse = {
  body: string;
  version: string;
};
// TODO(pt36 consent cutover): persist/activate PRODUCTION_TARGET_CONSENT through the server-side consent_versions selection path when dry_run_2026_05 is retired.

type SignupField =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'mobileNumber'
  | 'region'
  | 'residency'
  | 'consent'
  | 'hcaptcha';

export type SignupFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  region: string;
  isResident: boolean;
  consent: boolean;
};

export type SignupFormErrors = Partial<Record<SignupField, string>>;

const emptyValues: SignupFormValues = {
  consent: false,
  email: '',
  firstName: '',
  isResident: false,
  lastName: '',
  mobileNumber: '',
  region: '',
};

const sortedTtRegions = [...TT_REGIONS].sort((left, right) => left.localeCompare(right));
const signupAttributionStorageKey = 'trinidad_signup_attribution';
const attributionValueMaxLength = 120;
const attributionReferrerMaxLength = 512;
const sourceAliases = new Map<string, ApprovedSource>([
  ['adwords', 'google'],
  ['direct', 'direct'],
  ['facebook', 'facebook'],
  ['fb', 'facebook'],
  ['google', 'google'],
  ['google-ads', 'google'],
  ['ig', 'instagram'],
  ['instagram', 'instagram'],
  ['meta', 'facebook'],
  ['organic', 'organic'],
  ['referral', 'referral'],
  ['referer', 'referral'],
  ['referrer', 'referral'],
  ['seo', 'organic'],
  ['tik-tok', 'tiktok'],
  ['tiktok', 'tiktok'],
  ['unknown', 'unknown'],
]);

const researchTopics = [
  { kind: 'food', label: 'Groceries & food' },
  { kind: 'restaurant', label: 'Local restaurants' },
  { kind: 'money', label: 'Banking & money' },
  { kind: 'phone', label: 'Telecoms & internet' },
  { kind: 'beauty', label: 'Health & wellness' },
  { kind: 'music', label: 'Music & Carnival' },
  { kind: 'travel', label: 'Travel & tourism' },
  { kind: 'tech', label: 'Electronics' },
  { kind: 'tv', label: 'Streaming & media' },
  { kind: 'home', label: 'Public services' },
  { kind: 'sports', label: 'Sports & sponsors' },
  { kind: 'fashion', label: 'Local brands' },
];

const homeRewards = [
  {
    badge: 'Airtime only',
    meta: 'From TT$25.00',
    sub: 'Sent as prepaid phone credit to your Digicel or bMobile number',
    title: 'Mobile Airtime',
  },
];

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

type AttributionStorage = Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>;

export type SignupAttributionPayload = {
  gbraid?: string;
  gclid?: string;
  referrer: string;
  source: ApprovedSource;
  utm_campaign?: string;
  utm_content?: string;
  utm_medium?: string;
  utm_source?: string;
  utm_term?: string;
  wbraid?: string;
};

type SignupAttributionInput = {
  referrer: string;
  search: string;
  storage?: AttributionStorage | undefined;
};

function normalizeSourceToken(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

function approvedSourceFromValue(value: string | null): ApprovedSource | null {
  if (!value) {
    return null;
  }

  const source = normalizeSourceToken(value);

  if (APPROVED_SOURCES.includes(source as ApprovedSource)) {
    return source as ApprovedSource;
  }

  return sourceAliases.get(source) ?? null;
}

function sourceFromUtmSource(value: string | null): ApprovedSource | null {
  if (!value) {
    return null;
  }

  return approvedSourceFromValue(value) ?? 'unknown';
}

function sanitizeAttributionValue(value: string | null, maxLength = attributionValueMaxLength) {
  const sanitized = value?.replace(/[<>]/g, '').replace(/\s+/g, ' ').trim() ?? '';

  return sanitized ? sanitized.slice(0, maxLength) : undefined;
}

function readStoredSignupAttribution(
  storage: AttributionStorage | undefined,
): SignupAttributionPayload | null {
  if (!storage) {
    return null;
  }

  try {
    const stored = storage.getItem(signupAttributionStorageKey);

    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored) as Partial<SignupAttributionPayload>;
    const source = approvedSourceFromValue(parsed.source ?? null);

    return source
      ? {
          referrer: typeof parsed.referrer === 'string' ? parsed.referrer : '',
          source,
          ...(typeof parsed.utm_campaign === 'string' ? { utm_campaign: parsed.utm_campaign } : {}),
          ...(typeof parsed.utm_content === 'string' ? { utm_content: parsed.utm_content } : {}),
          ...(typeof parsed.utm_medium === 'string' ? { utm_medium: parsed.utm_medium } : {}),
          ...(typeof parsed.utm_source === 'string' ? { utm_source: parsed.utm_source } : {}),
          ...(typeof parsed.utm_term === 'string' ? { utm_term: parsed.utm_term } : {}),
          ...(typeof parsed.gbraid === 'string' ? { gbraid: parsed.gbraid } : {}),
          ...(typeof parsed.gclid === 'string' ? { gclid: parsed.gclid } : {}),
          ...(typeof parsed.wbraid === 'string' ? { wbraid: parsed.wbraid } : {}),
        }
      : null;
  } catch {
    return null;
  }
}

function storeSignupAttribution(
  storage: AttributionStorage | undefined,
  attribution: SignupAttributionPayload,
) {
  if (!storage) {
    return;
  }

  try {
    storage.setItem(signupAttributionStorageKey, JSON.stringify(attribution));
  } catch {
    storage.removeItem(signupAttributionStorageKey);
  }
}

function buildSignupAttribution(input: SignupAttributionInput): SignupAttributionPayload {
  const params = new URLSearchParams(input.search);
  const utmSource = sanitizeAttributionValue(params.get('utm_source'));
  const utmCampaign = sanitizeAttributionValue(params.get('utm_campaign'));
  const utmContent = sanitizeAttributionValue(params.get('utm_content'));
  const utmMedium = sanitizeAttributionValue(params.get('utm_medium'));
  const utmTerm = sanitizeAttributionValue(params.get('utm_term'));
  const gclid = sanitizeAttributionValue(params.get('gclid'));
  const gbraid = sanitizeAttributionValue(params.get('gbraid'));
  const wbraid = sanitizeAttributionValue(params.get('wbraid'));
  const referrer = sanitizeAttributionValue(input.referrer, attributionReferrerMaxLength) ?? '';
  const explicitSource = approvedSourceFromValue(params.get('source'));
  const source =
    sourceFromUtmSource(utmSource ?? null) ?? explicitSource ?? (referrer ? 'referral' : 'direct');
  const attribution: SignupAttributionPayload = {
    referrer,
    source,
  };

  if (utmCampaign) {
    attribution.utm_campaign = utmCampaign;
  }
  if (utmContent) {
    attribution.utm_content = utmContent;
  }
  if (utmMedium) {
    attribution.utm_medium = utmMedium;
  }
  if (utmSource) {
    attribution.utm_source = utmSource;
  }
  if (utmTerm) {
    attribution.utm_term = utmTerm;
  }
  if (gclid) {
    attribution.gclid = gclid;
  }
  if (gbraid) {
    attribution.gbraid = gbraid;
  }
  if (wbraid) {
    attribution.wbraid = wbraid;
  }

  return attribution;
}

function hasSignupAttributionSignal(input: SignupAttributionInput): boolean {
  const params = new URLSearchParams(input.search);

  return (
    Boolean(input.referrer.trim()) ||
    Boolean(params.get('source')) ||
    [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_content',
      'utm_term',
      'gclid',
      'gbraid',
      'wbraid',
    ].some((key) => Boolean(params.get(key)))
  );
}

export function captureSignupAttribution(input: SignupAttributionInput): SignupAttributionPayload {
  const attribution = buildSignupAttribution(input);

  storeSignupAttribution(input.storage, attribution);

  return attribution;
}

export function readSignupAttributionForSubmit(
  input: SignupAttributionInput,
): SignupAttributionPayload {
  if (hasSignupAttributionSignal(input)) {
    return captureSignupAttribution(input);
  }

  return readStoredSignupAttribution(input.storage) ?? buildSignupAttribution(input);
}

export function validateSignupForm(values: SignupFormValues): SignupFormErrors {
  const errors: SignupFormErrors = {};

  if (!values.firstName.trim()) {
    errors.firstName = 'First name is required.';
  }

  if (!values.lastName.trim()) {
    errors.lastName = 'Last name is required.';
  }

  if (!values.email.trim()) {
    errors.email = 'Email is required.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeEmail(values.email))) {
    errors.email = 'Enter a valid email address.';
  }

  if (values.mobileNumber.trim()) {
    try {
      normalizeTtMobileNumber(values.mobileNumber);
    } catch {
      errors.mobileNumber = 'Enter a valid Trinidad & Tobago mobile number.';
    }
  }

  if (!values.region.trim()) {
    errors.region = 'Region is required.';
  } else if (!(TT_REGIONS as readonly string[]).includes(values.region)) {
    errors.region = 'Choose a listed Trinidad & Tobago region.';
  }

  if (!values.isResident) {
    errors.residency = 'Confirm you are currently a resident of Trinidad and Tobago.';
  }

  if (!values.consent) {
    errors.consent = 'Consent is required to join.';
  }

  return errors;
}

export function signupSuccessMessage(payload: unknown): string {
  if (
    payload &&
    typeof payload === 'object' &&
    (payload as { status?: unknown }).status === 'held_for_review'
  ) {
    return 'Thanks. Your signup is under review, and we will email you when it is ready.';
  }

  return 'Check your email. We sent a sign-in link to confirm your signup.';
}

export function signupPhonePolicyError(status: number, payload: unknown): string | null {
  if (!payload || typeof payload !== 'object' || status !== 409) {
    return null;
  }

  const response = payload as {
    error?: unknown;
    errors?: { mobile_phone?: unknown };
    message?: unknown;
  };

  return response.error === 'signup_phone_already_registered' &&
    response.errors?.mobile_phone === duplicatePhonePolicyMessage &&
    response.message === duplicatePhonePolicyMessage
    ? duplicatePhonePolicyMessage
    : null;
}

function ErrorText({ children, id }: { children: string | undefined; id: string }) {
  if (!children) {
    return null;
  }

  return (
    <span className="err" id={id}>
      {children}
    </span>
  );
}

function Field({ children, invalid }: { children: ReactNode; invalid?: boolean }) {
  return <div className={`field ${invalid ? 'invalid' : ''}`}>{children}</div>;
}

async function fetchActiveConsent(): Promise<ActiveConsentResponse> {
  const response = await fetch(buildApiUrl('/api/v1/consent/active'), {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error('Active consent is unavailable');
  }

  const payload = (await response.json()) as unknown;

  if (!payload || typeof payload !== 'object') {
    throw new Error('Active consent response is invalid');
  }

  const body = (payload as { body?: unknown }).body;
  const version = (payload as { version?: unknown }).version;

  if (typeof body !== 'string' || !body.trim() || typeof version !== 'string' || !version.trim()) {
    throw new Error('Active consent response is invalid');
  }

  return { body: body.trim(), version: version.trim() };
}

const LandingIntro = memo(function LandingIntro({ onJoin }: { onJoin: () => void }) {
  return (
    <>
      <span id="top" />
      <DesignNav active="home" onJoin={onJoin} />
      <section className="relative overflow-hidden border-b border-[var(--line)] pb-10 pt-8">
        <div className="shell relative grid items-stretch gap-8 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="flex flex-col gap-5.5">
            <div className="design-eyebrow text-[var(--coral)]">Made for Trinidad &amp; Tobago</div>
            <h1 className="display text-[clamp(44px,11vw,92px)]">
              Share Your Opinion.
              <br />
              Get Rewarded.
            </h1>
            <p className="max-w-[540px] text-lg leading-normal text-[#1f2a37]">
              Take paid online surveys in Trinidad and Tobago and earn Digicel or bMobile airtime.
              Free to join, and your answers help shape the products and services around you.
            </p>
            <div className="flex flex-wrap gap-3">
              <button className="btn btn-lg btn-primary" onClick={onJoin} type="button">
                Join the Panel →
              </button>
              <a className="btn btn-lg" href="#how-it-works">
                How it works
              </a>
            </div>
          </div>

          <ProductUiComposition />
        </div>
        <div className="shell">
          <div className="mt-8 grid gap-3 border-t border-[var(--line)] pt-5 text-sm text-[var(--muted)] md:grid-cols-3">
            <span>Backed by Caribbean Market Research, est. 1970</span>
            <span>No fees</span>
            <span>Digicel or bMobile</span>
          </div>
          <ProductStatsBand className="mt-5" />
        </div>
      </section>

      <HomeHowItWorks />
      <Topics />
      <HomeRewards />
      <HomeAirtimeBlock />
      <Trust />
    </>
  );
});

const MemoizedDesignFooter = memo(DesignFooter);

export function LandingPage() {
  useDocumentTitle('Paid Online Surveys in Trinidad and Tobago, Earn Airtime | Trini Rewards');

  const [activeConsent, setActiveConsent] = useState<ActiveConsentResponse | null>(null);
  const [consentLoadFailed, setConsentLoadFailed] = useState(false);
  const [values, setValues] = useState<SignupFormValues>(emptyValues);
  const [errors, setErrors] = useState<SignupFormErrors>({});
  const [formMessage, setFormMessage] = useState<string>('');
  const [hasSubmittedSuccessfully, setHasSubmittedSuccessfully] = useState(false);
  const [hcaptchaToken, setHcaptchaToken] = useState('');
  const [isResidencyRejected, setIsResidencyRejected] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const productionSignupSmoke = readProductionSignupSmokeConfig();
  const hcaptchaSiteKey = productionSignupSmoke
    ? PRODUCTION_SMOKE_HCAPTCHA_SITE_KEY
    : getHcaptchaSiteKey();
  const signupAttribution = useMemo(
    () =>
      readSignupAttributionForSubmit({
        referrer: typeof document === 'undefined' ? '' : document.referrer,
        search: typeof window === 'undefined' ? '' : window.location.search,
        storage: typeof window === 'undefined' ? undefined : window.sessionStorage,
      }),
    [],
  );
  const recruitmentSource = signupAttribution.source;
  const hasEnteredSignupDetails =
    Boolean(values.firstName.trim()) &&
    Boolean(values.lastName.trim()) &&
    Boolean(values.email.trim()) &&
    Boolean(values.region.trim()) &&
    values.isResident &&
    values.consent;

  useEffect(() => {
    let isCurrent = true;

    fetchActiveConsent()
      .then((consent) => {
        if (!isCurrent) {
          return;
        }

        setActiveConsent(consent);
        setConsentLoadFailed(false);
      })
      .catch(() => {
        if (!isCurrent) {
          return;
        }

        setActiveConsent(null);
        setConsentLoadFailed(true);
      });

    return () => {
      isCurrent = false;
    };
  }, []);

  function updateValue<FieldName extends keyof SignupFormValues>(
    field: FieldName,
    value: SignupFormValues[FieldName],
  ) {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  }

  function messageFromRateLimit(retryAfterSeconds: number | undefined): string {
    const minutes = Math.max(1, Math.ceil((retryAfterSeconds ?? 60) / 60));
    return `Too many attempts. Try again in ${minutes} minutes.`;
  }

  const scrollToSignup = useCallback(() => {
    if (typeof document === 'undefined') {
      return;
    }

    document.getElementById('signup')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleHcaptchaError = useCallback(() => {
    setHcaptchaToken('');
    setErrors((current) => ({
      ...current,
      hcaptcha: 'Human verification failed.',
    }));
  }, []);

  const handleHcaptchaExpire = useCallback(() => {
    setHcaptchaToken('');
    setErrors((current) => ({
      ...current,
      hcaptcha: 'Human verification expired.',
    }));
  }, []);

  const handleHcaptchaVerify = useCallback((token: string) => {
    setHcaptchaToken(token);
    setErrors(({ hcaptcha: _hcaptcha, ...rest }) => rest);
  }, []);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormMessage('');

    const nextValues = { ...values };
    const nextErrors = validateSignupForm(nextValues);

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    if (!activeConsent) {
      setFormMessage('Consent text is not available yet. Please try again later.');
      return;
    }

    if (!hcaptchaToken) {
      setErrors((current) => ({ ...current, hcaptcha: 'Human verification is required.' }));
      return;
    }

    setIsSubmitting(true);

    try {
      const fingerprintPayload = await collectBrowserFingerprint();
      const signupPath = productionSignupSmoke
        ? productionSignupSmoke.signupPath
        : `/api/v1/signup?source=${encodeURIComponent(recruitmentSource)}`;
      const response = await fetch(buildApiUrl(signupPath), {
        body: JSON.stringify({
          consent_version: activeConsent.version,
          email: normalizeEmail(nextValues.email),
          first_name: nextValues.firstName.trim(),
          ...fingerprintPayload,
          attribution: signupAttribution,
          hcaptcha_token: hcaptchaToken,
          is_resident: nextValues.isResident,
          last_name: nextValues.lastName.trim(),
          ...(nextValues.mobileNumber.trim()
            ? { mobile_phone: normalizeTtMobileNumber(nextValues.mobileNumber) }
            : {}),
          recruitment_source: recruitmentSource,
          region: nextValues.region,
        }),
        headers: {
          'Content-Type': 'application/json',
          ...(productionSignupSmoke
            ? { 'X-Trini-Signup-Smoke': productionSignupSmoke.secret }
            : {}),
        },
        method: 'POST',
      });

      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as {
          error?: string;
          retry_after_seconds?: number;
        } | null;

        if (response.status === 400 && payload?.error === 'not_eligible_residency') {
          setHcaptchaToken('');
          setIsResidencyRejected(true);
          return;
        }

        if (response.status === 429) {
          setHcaptchaToken('');
          setFormMessage(messageFromRateLimit(payload?.retry_after_seconds));
          return;
        }

        const phonePolicyError = signupPhonePolicyError(response.status, payload);

        if (phonePolicyError) {
          setHcaptchaToken('');
          setErrors((current) => ({ ...current, mobileNumber: phonePolicyError }));
          return;
        }

        setHcaptchaToken('');
        setFormMessage('We could not send the link. Please try again later.');
        return;
      }

      const payload = await response.json().catch(() => null);

      markGoogleAdsVerifiedSignupPending();
      setHasSubmittedSuccessfully(true);
      setFormMessage(signupSuccessMessage(payload));
    } catch {
      setHcaptchaToken('');
      setFormMessage('We could not send the link. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="website-design min-h-screen bg-background text-foreground">
      <LandingIntro onJoin={scrollToSignup} />
      <section
        className="relative overflow-hidden border-y border-[var(--line)] bg-[var(--cream)] py-[70px]"
        id="signup"
      >
        <div className="shell relative grid items-start gap-8 lg:grid-cols-[1fr_1fr]">
          <div>
            <SectionHead
              eyebrow="Join the panel"
              title={
                <>
                  Be heard.
                  <br />
                  Get rewarded.
                </>
              }
            />
            <p className="max-w-[460px] text-[17px] leading-normal text-[#1f2a37]">
              Quick sign-up. We send a magic link to confirm your email, then invite you to studies
              that match your profile.
            </p>
            <ul className="mt-5 grid list-none gap-2 p-0">
              {['Free forever', 'Cash out from TT$25.00', 'Quit any time'].map((item) => (
                <li className="flex items-center gap-2.5 text-[15px] font-semibold" key={item}>
                  <BullseyeMarker action />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <form className="card flex flex-col gap-4 p-6" noValidate onSubmit={handleSubmit}>
            <div>
              <div className="h-2 overflow-hidden rounded-[8px] border border-[var(--line)] bg-white">
                <div className="h-full w-1/2 bg-[var(--coral)]" />
              </div>
              <div className="mt-2 font-mono text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--muted)]">
                STEP 1 OF 2 · DETAILS → EMAIL VERIFY
              </div>
            </div>
            <h3 className="display-md text-[26px]">Create your account</h3>

            {isResidencyRejected ? (
              <ResidencyRejectionScreen className="rounded-[8px] border border-[var(--line)] bg-white p-5" />
            ) : (
              <>
                <div className="grid gap-3.5 sm:grid-cols-2">
                  <Field invalid={Boolean(errors.firstName)}>
                    <label htmlFor="firstName">First name</label>
                    <input
                      aria-describedby={errors.firstName ? 'firstName-error' : undefined}
                      aria-invalid={Boolean(errors.firstName)}
                      autoComplete="given-name"
                      id="firstName"
                      name="firstName"
                      onChange={(event) => updateValue('firstName', event.target.value)}
                      value={values.firstName}
                    />
                    <ErrorText id="firstName-error">{errors.firstName}</ErrorText>
                  </Field>

                  <Field invalid={Boolean(errors.lastName)}>
                    <label htmlFor="lastName">Last name</label>
                    <input
                      aria-describedby={errors.lastName ? 'lastName-error' : undefined}
                      aria-invalid={Boolean(errors.lastName)}
                      autoComplete="family-name"
                      id="lastName"
                      name="lastName"
                      onChange={(event) => updateValue('lastName', event.target.value)}
                      value={values.lastName}
                    />
                    <ErrorText id="lastName-error">{errors.lastName}</ErrorText>
                  </Field>
                </div>

                <div className="grid gap-3.5 sm:grid-cols-2">
                  <Field invalid={Boolean(errors.email)}>
                    <label htmlFor="email">Email</label>
                    <input
                      aria-describedby={errors.email ? 'email-error' : undefined}
                      aria-invalid={Boolean(errors.email)}
                      autoComplete="email"
                      id="email"
                      inputMode="email"
                      name="email"
                      onChange={(event) => updateValue('email', event.target.value)}
                      type="email"
                      value={values.email}
                    />
                    <ErrorText id="email-error">{errors.email}</ErrorText>
                  </Field>

                  <Field invalid={Boolean(errors.mobileNumber)}>
                    <label htmlFor="mobileNumber">Mobile number</label>
                    <div className="flex overflow-hidden rounded-[8px] border border-[var(--line)] bg-white">
                      <span className="grid shrink-0 place-items-center border-r border-[var(--line)] bg-[var(--cream)] px-3 text-sm font-extrabold">
                        +1 868
                      </span>
                      <input
                        aria-describedby={errors.mobileNumber ? 'mobileNumber-error' : undefined}
                        aria-invalid={Boolean(errors.mobileNumber)}
                        autoComplete="tel-national"
                        className="min-w-0 flex-1 border-0 bg-transparent"
                        id="mobileNumber"
                        inputMode="numeric"
                        maxLength={7}
                        name="mobileNumber"
                        onChange={(event) => updateValue('mobileNumber', event.target.value)}
                        pattern="[0-9]{7}"
                        placeholder="5550199"
                        type="tel"
                        value={values.mobileNumber}
                      />
                    </div>
                    <p className="mt-1 text-xs font-semibold text-[var(--muted)]">
                      This is where your rewards arrive as phone credit.
                    </p>
                    <ErrorText id="mobileNumber-error">{errors.mobileNumber}</ErrorText>
                  </Field>

                  <Field invalid={Boolean(errors.region)}>
                    <label htmlFor="region">Region</label>
                    <select
                      aria-describedby={errors.region ? 'region-error' : undefined}
                      aria-invalid={Boolean(errors.region)}
                      id="region"
                      name="region"
                      onChange={(event) => updateValue('region', event.target.value)}
                      value={values.region}
                    >
                      <option value="">Select region</option>
                      {sortedTtRegions.map((region) => (
                        <option key={region} value={region}>
                          {region}
                        </option>
                      ))}
                    </select>
                    <ErrorText id="region-error">{errors.region}</ErrorText>
                  </Field>
                </div>

                <input name="consent_version" type="hidden" value={activeConsent?.version ?? ''} />
                <input name="source" type="hidden" value={recruitmentSource} />

                <HcaptchaWidget
                  onError={handleHcaptchaError}
                  onExpire={handleHcaptchaExpire}
                  onVerify={handleHcaptchaVerify}
                  productionSmoke={Boolean(productionSignupSmoke)}
                  siteKey={hcaptchaSiteKey}
                  token={hcaptchaToken}
                />
                <ErrorText id="hcaptcha-error">{errors.hcaptcha}</ErrorText>

                <div className="field">
                  <label htmlFor="consent-text">Consent text</label>
                  <div
                    className="max-h-[130px] overflow-y-auto rounded-[8px] border border-[var(--line)] bg-white p-3.5 text-[13px] leading-normal text-[#2A2A2A]"
                    id="consent-text"
                  >
                    {activeConsent ? (
                      <p className="whitespace-pre-wrap">{activeConsent.body}</p>
                    ) : (
                      <p>
                        {consentLoadFailed
                          ? 'Consent text is not available yet.'
                          : 'Consent text loading...'}
                      </p>
                    )}
                  </div>
                </div>

                <label className="flex cursor-pointer items-start gap-3 rounded-[8px] border border-[var(--line)] bg-white p-3.5">
                  <input
                    aria-describedby={errors.residency ? 'residency-error' : undefined}
                    aria-invalid={Boolean(errors.residency)}
                    checked={values.isResident}
                    className="mt-0.5 h-[22px] w-[22px] shrink-0 accent-[var(--coral)]"
                    id="residency"
                    name="is_resident"
                    onChange={(event) => updateValue('isResident', event.target.checked)}
                    type="checkbox"
                  />
                  <span>
                    <span className="block text-sm font-bold leading-normal">
                      I confirm I am currently a resident of Trinidad and Tobago
                    </span>
                    <ErrorText id="residency-error">{errors.residency}</ErrorText>
                  </span>
                </label>

                <label className="flex cursor-pointer items-start gap-3 rounded-[8px] border border-[var(--line)] bg-white p-3.5">
                  <input
                    aria-describedby={errors.consent ? 'consent-error' : undefined}
                    aria-invalid={Boolean(errors.consent)}
                    checked={values.consent}
                    className="mt-0.5 h-[22px] w-[22px] shrink-0 accent-[var(--coral)]"
                    id="consent"
                    name="consent"
                    onChange={(event) => updateValue('consent', event.target.checked)}
                    type="checkbox"
                  />
                  <span>
                    <span className="block text-sm font-bold leading-normal">
                      I consent to Trini Rewards contacting me by email and phone about research
                      opportunities, and storing my signup details for verification.
                    </span>
                    <span className="mt-1 block font-mono text-xs text-[var(--muted)]">
                      Consent version{' '}
                      <span className="font-bold text-[var(--ink)]">
                        {activeConsent?.version ?? 'loading'}
                      </span>
                    </span>
                    <ErrorText id="consent-error">{errors.consent}</ErrorText>
                  </span>
                </label>

                {formMessage ? (
                  <output className="rounded-[8px] border border-[var(--line)] bg-white p-3 text-sm font-bold">
                    {formMessage}
                  </output>
                ) : null}

                <button
                  className="btn btn-lg btn-primary"
                  disabled={
                    isSubmitting ||
                    hasSubmittedSuccessfully ||
                    (hasEnteredSignupDetails && !hcaptchaToken)
                  }
                  type="submit"
                >
                  {isSubmitting ? 'Joining...' : 'Join Trini Rewards'}
                </button>
              </>
            )}
          </form>
        </div>
      </section>
      <MemoizedDesignFooter />
    </main>
  );
}

function HomeHowItWorks() {
  const steps = [
    {
      body: 'Name, email, mobile number, region. Magic link to your inbox to confirm. Under a minute.',
      n: '1',
      title: 'Join the panel',
    },
    {
      body: 'We invite you when a study matches your profile. Most launch studies fit the 5-45 min study range, on mobile, on your time.',
      n: '2',
      title: 'Take studies that fit you',
    },
    {
      body: 'Earn TT$ airtime value and cash out from TT$25.00 as prepaid mobile airtime.',
      n: '3',
      title: 'Get paid',
    },
  ];

  return (
    <section className="relative py-[60px]" id="how-it-works">
      <div className="shell">
        <SectionHead eyebrow="How it works" title="Three easy steps." />
        <div className="mt-7 grid gap-[18px] [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]">
          {steps.map((step) => (
            <article
              className="card relative flex flex-col gap-3.5 overflow-hidden p-6"
              key={step.n}
            >
              <div className="design-eyebrow text-[var(--muted)]">0{step.n}</div>
              <div className="display-md text-[28px]">{step.title}</div>
              <p className="m-0 text-[15px] font-medium leading-normal">{step.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Topics() {
  return (
    <section
      className="ink-band relative overflow-hidden py-[60px]"
      data-ink-band="home-topics"
      id="topics"
    >
      <BullseyeDevice />
      <div className="shell relative">
        <div className="mb-3.5">
          <div className="design-eyebrow text-[var(--coral)]">Topics you'll shape</div>
          <h2 className="display ink-band-title mt-2">
            Brands want to hear from you
            <RedPeriod />
          </h2>
        </div>
        <p className="-mt-2 max-w-[540px] text-base leading-normal text-[var(--muted)]">
          Studies cover categories that matter to Trinbagonian consumers. Your everyday choices help
          shape what local and regional teams do next.
        </p>
        <div className="mt-7 grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(140px,1fr))]">
          {researchTopics.map((topic) => (
            <div
              className="ink-band-tile flex flex-col gap-2.5 rounded-[8px] p-4"
              key={topic.label}
            >
              <TopicIcon color="rgba(255,255,255,.12)" kind={topic.kind} />
              <div className="display-md text-xl">{topic.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HomeRewards() {
  return (
    <section className="relative py-[70px]" id="rewards">
      <div className="shell">
        <SectionHead
          eyebrow="Rewards"
          title={
            <>
              Real rewards.
              <br />
              Local options.
            </>
          }
        />
        <p className="-mt-2 max-w-[560px] text-base leading-normal text-[var(--muted)]">
          Stack TT$ airtime value and turn your rewards into prepaid mobile airtime for your
          Trinidad and Tobago number.
        </p>
        <div className="mt-8 grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]">
          {homeRewards.map((reward) => (
            <article
              className="card reward-feature-card reward-feature-card-light relative flex min-h-[220px] flex-col gap-2.5 overflow-hidden p-[26px]"
              data-reward-card="home-airtime"
              key={reward.title}
            >
              <div aria-hidden="true" className="reward-card-rings reward-card-rings-light">
                <span />
                <span />
                <span />
                <span />
              </div>
              <div className="reward-feature-label">
                <span aria-hidden="true" className="bullseye-marker bullseye-marker-action" />
                {reward.badge}
              </div>
              <div className="reward-feature-title display-md mt-4 leading-tight">
                {reward.title}
              </div>
              <div className="reward-feature-stat">
                {reward.meta}
                <span aria-hidden="true" className="reward-stat-stop">
                  .
                </span>
              </div>
              <div className="reward-feature-body">{reward.sub}</div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function HomeAirtimeBlock() {
  const facts = ['Digicel or bMobile airtime', 'Cash out from TT$25', 'Sent to your phone'];

  return (
    <section className="border-y border-[var(--line)] bg-[var(--tr-bg-soft)] py-[70px]">
      <div className="shell">
        <div className="card grid items-center gap-6 p-7 md:grid-cols-[1fr_0.8fr]">
          <div>
            <SectionHead
              eyebrow="Airtime rewards"
              title={
                <>
                  Earn airtime taking surveys in{' '}
                  <span className="text-[var(--coral)]">Trinidad.</span>
                </>
              }
            />
            <p className="max-w-[620px] text-[16px] leading-normal text-[#1f2a37]">
              Complete paid online surveys and turn your rewards into prepaid Digicel or bMobile
              airtime. Once your spendable balance reaches TT$25, cash out and the airtime top-up is
              sent to your phone.
            </p>
          </div>
          <ul className="m-0 grid list-none gap-3 p-0">
            {facts.map((fact) => (
              <li className="flex items-center gap-3 font-bold text-[var(--ink)]" key={fact}>
                <BullseyeMarker action />
                {fact}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function Trust() {
  const points = [
    {
      body: 'We never sell or share your personal data with brands. Ever.',
      icon: 'lock',
      title: 'Your opinions stay confidential',
    },
    {
      body: 'Designed locally with studies and rewards that fit our islands.',
      icon: 'heart',
      title: 'Made for real Trinis & Tobagonians',
    },
    {
      body: 'Quick, thumb-friendly, and easy on data. Take them on the maxi.',
      icon: 'phone',
      title: 'Studies made for mobile',
    },
  ];

  return (
    <section className="relative py-[70px]" id="trust">
      <div className="shell">
        <div className="grid items-center gap-8 [grid-template-columns:repeat(auto-fit,minmax(280px,1fr))]">
          <div>
            <SectionHead eyebrow="Trust & community" title="Built for our voices." />
            <p className="text-base leading-normal text-[#1f2a37]">
              Trini Rewards is the panel for everyday people across the islands, from POS to
              Scarborough, San Fernando to Sangre Grande. Your voice helps shape what brands bring
              to T&amp;T.
            </p>
          </div>
          <div className="grid gap-3.5">
            {points.map((point) => (
              <article className="card flex items-start gap-4 p-5" key={point.title}>
                <TrustIcon kind={point.icon} />
                <div>
                  <div className="display-md mb-1 text-lg">{point.title}</div>
                  <p className="m-0 text-sm leading-normal text-[var(--muted)]">{point.body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustIcon({ kind }: { kind: string }) {
  return (
    <div className="grid h-[52px] w-[52px] shrink-0 place-items-center rounded-[8px] border border-[var(--line)] bg-[var(--cream)]">
      {kind === 'lock' ? (
        <svg aria-hidden="true" fill="none" height="22" viewBox="0 0 24 24" width="22">
          <rect height="10" rx="2" stroke="var(--ink)" strokeWidth="2.4" width="16" x="4" y="11" />
          <path d="M8 11 V 8 a 4 4 0 0 1 8 0 V 11" stroke="var(--ink)" strokeWidth="2.4" />
        </svg>
      ) : null}
      {kind === 'heart' ? (
        <svg aria-hidden="true" height="22" viewBox="0 0 24 24" width="22">
          <path
            d="M12 21 C 4 14, 4 7, 8 5 C 10 4, 12 6, 12 8 C 12 6, 14 4, 16 5 C 20 7, 20 14, 12 21 Z"
            fill="var(--ink)"
          />
        </svg>
      ) : null}
      {kind === 'phone' ? (
        <svg aria-hidden="true" height="22" viewBox="0 0 24 24" width="22">
          <rect
            fill="none"
            height="18"
            rx="2.5"
            stroke="var(--ink)"
            strokeWidth="2.4"
            width="12"
            x="6"
            y="3"
          />
          <circle cx="12" cy="18" fill="var(--ink)" r="1.2" />
        </svg>
      ) : null}
    </div>
  );
}
